import { Vector3 } from "three";
import { Previewable, PreviewManager } from "../object/PreviewManager";
import {
  InputPointerType,
  InputEventType,
  InputMultiPointerEvent,
  ISwipeArgs,
  IPinchArgs,
} from "../utils/input-handler/interface";
import { Viewer } from "../viewer";
import * as Config from "../config/ViewerConfig";
import { IMeasurementPreviewCreation } from "./types";
import { generatePreviewMeasurmentId } from "./utils";

export class AreaMeasurementCreationPreview
  implements IMeasurementPreviewCreation {
  points: Vector3[];
  private _viewer: Viewer;
  private _preview: PreviewManager;
  private _pinIds: number[];
  private _lineId: number;
  private _meshId: number;
  private _isPointPickedOnStartDrag = false;

  private _resolve: any;
  private _reject: any;

  private _id: number;

  public get id() {
    return this._id;
  }

  constructor(viewer: Viewer, preview: PreviewManager) {
    this._viewer = viewer;
    this._preview = preview;
    this.points = [];
    this._pinIds = [];
    this._id = generatePreviewMeasurmentId.next().value as number;
  }

  public startMeasurement(): Promise<Vector3[]> {
    return new Promise((resolve, reject) => {
      this._resolve = resolve;
      this._reject = reject;
      const previewables = this._preview.init([
        {
          type: Previewable.PIN,
          count: 1,
          color: Config.PREVIEW_MEASUREMENT_COLOR,
        },
        {
          type: Previewable.LINE_LOOP,
          count: 1,
          color: Config.PREVIEW_MEASUREMENT_COLOR,
        },
        {
          type: Previewable.MESH,
          count: 1,
          color: Config.PREVIEW_MEASUREMENT_COLOR,
        },
      ]);
      this._pinIds.push(...previewables[0].ids);
      this._lineId = previewables[1].ids[0];
      this._meshId = previewables[2].ids[0];
    });
  }

  public finishMeasurement(status = true, message = "") {
    if (status && this.points.length >= 3) {
      this._resolve([...this.points]);
    } else {
      this._reject({ message, id: this.id });
    }
    this.dispose();
  }

  public dispose() {
    this.points.length = 0;
    this._preview.dispose([...this._pinIds, this._lineId, this._meshId]);
    const event = new CustomEvent('onPointClick', { detail: { count: this.points.length } });
    document.dispatchEvent(event);
  }

  private updatePosition(point: Vector3) {
    //updates for the latest one
    const pinId = this._pinIds[this._pinIds.length - 1];
    this._preview.updatePreview(pinId, {
      position: point,
      type: Previewable.PIN,
      visible: true,
    });
    if (this.points.length > 1) {
      this._preview.updatePreview(this._lineId, {
        type: Previewable.LINE_LOOP,
        points: [...this.points, point],
        visible: true,
      });
    }
    if (this.points.length > 2) {
      this._preview.updatePreview(this._meshId, {
        type: Previewable.MESH,
        points: [...this.points, point],
        visible: true,
      });
    }
  }

  public handlePointerEvent(
    pointer: { pointerType: InputPointerType; eventType: InputEventType },
    event: PointerEvent
  ): void {
    switch (pointer.eventType) {
      case InputEventType.POINTER_HOVER: //touch cannot hover
        const point = this._viewer.intersectPointCloud(event);
        point && this.updatePosition(point);
        break;
      case InputEventType.CLICK: {
        //if possible click will resolve
        const currentPinId = this._pinIds[this._pinIds.length - 1];
        const pin = this._preview.getPin(currentPinId);

        if (pointer.pointerType === InputPointerType.SECONDARY) {
          // 右クリックで計測終了
          if (this.points.length >= 3) {
            this._viewer.finishMeasurementCreation();
          }
          // 2点以下の場合はundoする
          else {
            for (let i = 0; i <= this.points.length; ++i) {
              this._viewer.undoMeasurement();
            }
          }
        }
        else {
          if (!pin.visible) {
            return;
          }
          if (event.pointerType === "touch") {
            //shift the point downward
            let { clientX, clientY } = event;
            clientY += Config.TOUCH_OFFSET;
            const point = this._viewer.intersectPointCloud({ clientX, clientY });
            point && this.updatePosition(point);
          }
          if (pointer.pointerType === InputPointerType.PRIMARY) {
            const point = this._preview.getPinPosition(currentPinId);

            if (point) {
              this.points.push(point.clone());
              const event = new CustomEvent('onPointClick', { detail: { count: this.points.length } });
              document.dispatchEvent(event);
              this._viewer.setPointsToMeasurement(undefined, this.points);

              const pins = this._preview.addPreviewable({
                type: Previewable.PIN,
                count: 1,
                color: Config.PREVIEW_MEASUREMENT_COLOR,
              });
              this._pinIds.push(...pins.ids);
            }
          }
        }

        break;
      }
      case InputEventType.START_DRAG: //Start drag cannot resolve, end drag can resolve
        let { clientX, clientY } = event;
        if (event.pointerType === "touch") {
          //shift the point downward
          clientY += Config.TOUCH_OFFSET;
          // this._viewer.enableControl({ rotate: false });
        }
        if (pointer.pointerType === InputPointerType.PRIMARY) {
          const point = this._viewer.intersectPointCloud({ clientX, clientY });
          if (point) {
            this._viewer.enableControl({ rotate: false });
            this.updatePosition(point);
            this._isPointPickedOnStartDrag = true;
          }
          // point && this.updatePosition(point);
          // if (point) {
          //   this.points.push(point.clone());
          // }
        }
        break;
      case InputEventType.DRAGGED: {
        // end drag is required to resolve
        let { clientX, clientY } = event;

        if (event.pointerType === "touch") {
          //shift the point downward
          if (this._isPointPickedOnStartDrag) {
            clientY += Config.TOUCH_OFFSET;
            const point = this._viewer.intersectPointCloud({
              clientX,
              clientY,
            });
            point && this.updatePosition(point);
          }
        }

        break;
      }
      case InputEventType.END_DRAG: {
        if (pointer.pointerType === InputPointerType.PRIMARY) {
          this._viewer.enableControl({ pan: true, rotate: true });
          if (event.pointerType === "touch") {
            if (this._isPointPickedOnStartDrag) {
              this._isPointPickedOnStartDrag = false;

              const point = this._preview.getPinPosition(this._pinIds[this._pinIds.length - 1]);

              if (point) {
                this.points.push(point.clone());
                const event = new CustomEvent('onPointClick', { detail: { count: this.points.length } });
                document.dispatchEvent(event);
                this._viewer.setPointsToMeasurement(undefined, this.points);

                const pins = this._preview.addPreviewable({
                  type: Previewable.PIN,
                  count: 1,
                  color: Config.PREVIEW_MEASUREMENT_COLOR,
                });
                this._pinIds.push(...pins.ids);
              }
            }
          }
        }
        break;
      }
    }
  }

  public handleMultiplePointerEvent(
    eventType: InputMultiPointerEvent,
    args: ISwipeArgs | IPinchArgs
  ): void {
    console.warn("Multiple Pointer not implemented");
    // throw new Error("Method not implemented.");
  }

  public setPoints(points: Vector3[]): void {
    this.points = points.map((p) => p.clone());

    if (this.points.length > this._pinIds.length) {
      const pins = this._preview.addPreviewable({
        type: Previewable.PIN,
        count: this.points.length - this._pinIds.length + 1,
        color: Config.PREVIEW_MEASUREMENT_COLOR,
      });
      this._pinIds.push(...pins.ids);
    } else if (this.points.length < this._pinIds.length) {
      const idsToDispose = this._pinIds.splice(this.points.length);
      this._preview.dispose(idsToDispose);
      //one for current hovering around
      const pins = this._preview.addPreviewable({
        type: Previewable.PIN,
        count: 1,
        color: Config.PREVIEW_MEASUREMENT_COLOR,
      });
      this._pinIds.push(...pins.ids);
    }

    for (let i = 0; i < this.points.length; ++i) {
      this._preview.updatePreview(this._pinIds[i], {
        position: this.points[i],
        type: Previewable.PIN,
        visible: true,
      });
    }
    if (this.points.length > 1) {
      this._preview.updatePreview(this._lineId, {
        type: Previewable.LINE_LOOP,
        points: [...this.points],
        visible: true,
      });
    } else {
      this._preview.updatePreview(this._lineId, {
        type: Previewable.LINE_LOOP,
        points: [],
        visible: false,
      });
    }
    if (this.points.length > 2) {
      this._preview.updatePreview(this._meshId, {
        type: Previewable.MESH,
        points: [...this.points],
        visible: true,
      });
    } else {
      this._preview.updatePreview(this._meshId, {
        type: Previewable.MESH,
        points: [],
        visible: false,
      });
    }
    const event = new CustomEvent('onPointClick', { detail: { count: this.points.length } });
    document.dispatchEvent(event);
    this._viewer.updateRenderer();
  }
}
